// ============================================================================
// Configuration de la marque — SPAR
// Point unique de personnalisation : nom, couleurs, visuels des lots.
// Pour décliner le jeu sur une autre marque, seul ce fichier change.
// ============================================================================

// Identifiant envoyé au backend (paramètre ?brand=) — doit correspondre à
// l'enum Brand côté API.
export const BRAND = process.env.REACT_APP_BRAND || 'spar';

// Libellés affichés
export const BRAND_NAME = 'SPAR';           // Titres, sidebar
export const BRAND_NAME_UPPER = 'SPAR';     // Bandeaux et footers en capitales
export const BRAND_LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;      // Logo officiel, extrait du visuel de la carte
export const BRAND_YEAR = new Date().getFullYear();

// URL publique du jeu — sert aux QR codes et aux liens WhatsApp
export const GAME_BASE_URL =
  process.env.REACT_APP_GAME_BASE_URL ||
  `${window.location.origin}${process.env.PUBLIC_URL || ''}`;

// ---------------------------------------------------------------------------
// Mécanique de participation
// Un seul tour de jeu dès AMOUNT_PER_PLAY FCFA de produits éligibles,
// quel que soit le montant au-delà. Doit rester aligné sur
// SPAR_AMOUNT_PER_PLAY dans backend/routes/hostess.py.
// ---------------------------------------------------------------------------
export const AMOUNT_PER_PLAY = 1000;
export const ELIGIBLE_PRODUCTS = 'produits Source du Pays';
export const ELIGIBLE_PRODUCTS_DETAIL = 'eaux, boissons gazeuses et boissons énergisantes Source du Pays';

// Couleurs de la charte — relevées sur le visuel officiel de la carte.
// Doivent rester identiques aux variables --brand-* de index.css.
export const COLORS = {
  primary: '#E21F27',      // Rouge SPAR
  primaryDark: '#B5141B',
  accent: '#029046',       // Vert SPAR
  dark: '#1a1a1a',
  light: '#ffffff',
  gray: '#f5f5f5',
};

// ---------------------------------------------------------------------------
// Carte à gratter : habillage de la surface à gratter
// COVER_IMAGE est dessinée sur le canvas ; si le fichier est absent, le
// dessin de secours ci-dessous (lignes + bouton) est généré à la volée.
// ---------------------------------------------------------------------------
export const SCRATCH_COVER_IMAGE = 'carte-spar.jpg';
export const SCRATCH_COVER_LINES = [
  'SACS À DOS – PARAPLUIES',
  'CASQUETTES – POLOS SPAR',
];
export const SCRATCH_CTA = 'GRATTER ICI !';

// ---------------------------------------------------------------------------
// Visuels des lots
// PRIZE_IMAGES : clé = prize_type défini côté backend (populate_spar_prizes.py)
// LABEL_IMAGES : secours par libellé, insensible à la casse et aux accents
// Les fichiers sont servis depuis public/ (ou REACT_APP_ASSET_BASE_URL).
// ---------------------------------------------------------------------------
export const PRIZE_IMAGES = {
  spar_sac_a_dos: 'sac-a-dos-spar.jpg',
  spar_parapluie: 'parapluie-spar.jpg',
  spar_casquette: 'casquette-spar.jpg',
  spar_tshirt: 'tshirt-spar.jpg',
  spar_reessayer: null,
  try_again_1: null,
  reessayer: null,
};

// Secours par libellé — insensible à la casse et aux accents
export const LABEL_IMAGES = {
  'sac a dos spar': 'sac-a-dos-spar.jpg',
  'sac a dos': 'sac-a-dos-spar.jpg',
  'parapluie spar': 'parapluie-spar.jpg',
  'parapluie': 'parapluie-spar.jpg',
  'casquette spar': 'casquette-spar.jpg',
  'casquette': 'casquette-spar.jpg',
  'tshirt spar': 'tshirt-spar.jpg',
  't shirt spar': 'tshirt-spar.jpg',
  'tshirt': 'tshirt-spar.jpg',
  't shirt': 'tshirt-spar.jpg',
};

// Lots présentés dans le guide hôtesse (page /guide).
// Doit refléter SPAR_PRIZES de backend/populate_spar_prizes.py.
export const GUIDE_PRIZES = [
  { name: 'Sac à dos',      img: '/sac-a-dos-spar.jpg', chance: '10%', color: '#0E3B22' },
  { name: 'Parapluie',      img: '/parapluie-spar.jpg', chance: '13%', color: '#009640' },
  { name: 'T-shirt',        img: '/tshirt-spar.jpg',    chance: '15%', color: '#B00510' },
  { name: 'Casquette',      img: '/casquette-spar.jpg', chance: '22%', color: '#E30613' },
  { name: 'Réessayez',      img: null, emoji: '🔄',     chance: '40%', color: '#78909C' },
];

const IMAGE_BASE_URL =
  process.env.REACT_APP_ASSET_BASE_URL ||
  `${window.location.origin}${process.env.PUBLIC_URL || ''}`;

export const normalizeKey = (value) => {
  if (!value) return '';
  return value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

// Accepte une URL déjà complète (http, //, /, data:) ou un simple nom de fichier
export const normalizeAssetUrl = (value) => {
  if (!value) return null;
  const trimmed = value.toString().trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  return `${IMAGE_BASE_URL}/${encodeURIComponent(trimmed)}`;
};

// Résout le visuel d'un lot : type exact, puis libellé, puis nom.
export const getPrizeImageUrl = (prizeType, prizeLabel, prizeName) => {
  const typeKey = normalizeKey(prizeType).replace(/ /g, '_');
  const direct = PRIZE_IMAGES[prizeType] ?? PRIZE_IMAGES[typeKey];
  const filename =
    direct ||
    LABEL_IMAGES[normalizeKey(prizeLabel)] ||
    LABEL_IMAGES[normalizeKey(prizeName)];
  if (!filename) return null;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(filename)}`;
};

export { IMAGE_BASE_URL };
